import Vue from 'vue';
import moment from 'moment';

// Admin display mapping for repeat prescription cycles (docs/shared/CONVENTIONS.md §1).
// The backend processStatus vocabulary is ACTIVE | PROCESSING | REJECTED | COMPLETED
// (verified against the deployed dev backend; see the doctor portal's
// repeat-prescriptions.models.ts) and is never shown raw. PROCESSING mid-retry reads
// as Active — the retry situation shows only through the payment-attempts and
// next-action columns; only exhausted retries surface as Payment Failed.

export const RETRY_LIMIT = 4; // the immediate charge + 3 automatic retries

// Issued repeats come from the per-flow schedule statuses: the backend marks a
// flow COMPLETED when its payment captures — including flow 1 at checkout, so
// a fresh cycle already counts 1. Cycles created before flow statuses existed
// carry none, so the flows before the one in flight are the fallback.
export function issuedRepeats(item) {
  if (item.processStatus === 'COMPLETED') {
    return item.totalRepeatFlow || 0;
  }
  const completedFlows = (item.orderRepeatPRSCFlowDetails || [])
    .filter(flow => String(flow.status || '').toUpperCase() === 'COMPLETED')
    .length;
  return Math.max(completedFlows, (item.currentRepeatFlow || 0) - 1, 0);
}

export function failedAttemptsForCurrentFlow(item) {
  return (item.paymentAttempts || [])
    .filter(a => a.repeatFlow === item.currentRepeatFlow &&
      String(a.status || '').toUpperCase() !== 'CAPTURED')
    .length;
}

// Clinician has reviewed a repeat whose payment is still outstanding — the
// backend never advances processStatus past ACTIVE, so this is derived from
// the review references, the per-flow schedule statuses, and the payment
// attempts. A reviewed flow counts as paid when the backend marked its flow
// COMPLETED (flow 1 is settled at checkout, outside paymentAttempts) or a
// CAPTURED attempt exists for it. Matched on each review row's own repeatFlow
// (not currentRepeatFlow) so it stays correct if the flow counter is off.
export function isAwaitingPayment(item) {
  if (item.processStatus === 'COMPLETED' || item.processStatus === 'REJECTED' ||
      item.processStatus === 'CANCELED') {
    return false;
  }
  const capturedFlows = new Set((item.paymentAttempts || [])
    .filter(a => String(a.status || '').toUpperCase() === 'CAPTURED')
    .map(a => a.repeatFlow));
  const completedFlows = new Set((item.orderRepeatPRSCFlowDetails || [])
    .filter(flow => String(flow.status || '').toUpperCase() === 'COMPLETED')
    .map(flow => flow.flowCount));
  return (item.clinicianReviewReferences || [])
    .filter(r => String(r.status || '').toUpperCase() === 'REVIEWED')
    .some(r => !capturedFlows.has(r.repeatFlow) && !completedFlows.has(r.repeatFlow));
}

export function isPaymentExhausted(item) {
  return (item.processStatus === 'PROCESSING' || isAwaitingPayment(item)) &&
    failedAttemptsForCurrentFlow(item) >= RETRY_LIMIT;
}

// Mirrors the backend's review window (repeatPRSCReviewEngine): a repeat only
// becomes reviewable once its nextScheduledDate is within 7 days. Further out
// it is simply upcoming. Missing/unparseable dates count as due so a cycle
// never hides in Upcoming by accident.
export function isDueForReview(item) {
  if (!item.nextScheduledDate) {
    return true;
  }
  const due = moment(item.nextScheduledDate, 'YYYY-MM-DD');
  if (!due.isValid()) {
    return true;
  }
  return due.diff(moment().startOf('day'), 'days') <= 7;
}

/** Backend cancel API only accepts cycles in ACTIVE (see rejectRepeatPRSC). */
export function canCancelRepeatCycle (processStatus) {
  return String(processStatus || '').toUpperCase() === 'ACTIVE';
}

export function adminDisplayStatus(item) {
  switch (item.processStatus) {
    case 'PROCESSING':
      return isPaymentExhausted(item) ? 'PAYMENT_FAILED' : 'PENDING_PAYMENT';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'REJECTED':
    case 'CANCELED': // the backend's current cancellation value (single L)
      return 'CANCELLED';
    case 'ACTIVE':
    default:
      if (isAwaitingPayment(item)) {
        return isPaymentExhausted(item) ? 'PAYMENT_FAILED' : 'PENDING_PAYMENT';
      }
      if (item.processStatus === 'ACTIVE') {
        return isDueForReview(item) ? 'PENDING_REVIEW' : 'UPCOMING';
      }
      return item.processStatus || null;
  }
}

export function repeatCycleStatusLabel(displayStatus) {
  switch (displayStatus) {
    case 'PENDING_REVIEW':
      return 'Pending Review';
    case 'PENDING_PAYMENT':
      return 'Pending Payment';
    case 'UPCOMING':
      return 'Upcoming';
    case 'PAYMENT_FAILED':
      return 'Payment Failed';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'ACTIVE':
      return 'Active';
    default:
      return displayStatus || '—';
  }
}

export function repeatStatusVariant(displayStatus) {
  switch (displayStatus) {
    case 'PAYMENT_FAILED':
    case 'CANCELLED':
      return 'danger';
    case 'PENDING_PAYMENT':
      return 'warning';
    case 'PENDING_REVIEW':
      return 'info';
    case 'UPCOMING':
      return 'secondary';
    default:
      // ACTIVE, COMPLETED
      return 'success';
  }
}

Vue.filter('repeatCycleStatus', repeatCycleStatusLabel);
